// client/src/pages/gallery/DisplayGallery.js
import React, { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../../api/api';
import { SERVER_URL } from '../../constants/server-type';
import { toast } from 'react-toastify';

import './gallery.css';
import PrintAccounting from '../printManager/PrintAccounting';
import { requestPrint, admissionMessage } from '../printManager/printAccountingApi';

function buildRows(items, primaryView = 'landscape', maxPerRow = 6) {

  const rows = [];
  let currentRow = [];

  const MAX_PER_ROW = maxPerRow;

  function pushRow() {
    if (currentRow.length) {
      rows.push(currentRow);
      currentRow = [];
    }
  }

  let i = 0;

  while (i < items.length) {

    const item = items[i];

    // =========================
    // LANDSCAPE PRIMARY
    // =========================
    if (primaryView === 'landscape') {

      // landscape item
      if (item.orientation === 'landscape') {

        currentRow.push({
          type: 'landscape',
          items: [item]
        });

        if (currentRow.length >= MAX_PER_ROW) {
          pushRow();
        }

        i++;
        continue;
      }

      // portrait handling
      const next = items[i + 1];

      // portrait pair
      if (next && next.orientation === 'portrait') {

        currentRow.push({
          type: 'portrait-pair',
          items: [item, next]
        });

        if (currentRow.length >= MAX_PER_ROW) {
          pushRow();
        }

        i += 2;
        continue;
      }

      // single portrait
      currentRow.push({
        type: 'portrait-single',
        items: [item]
      });

      if (currentRow.length >= MAX_PER_ROW) {
        pushRow();
      }

      i++;
      continue;
    }

    // =========================
    // PORTRAIT PRIMARY
    // =========================
    else {

      if (item.orientation === 'portrait') {

        currentRow.push({
          type: 'portrait',
          items: [item]
        });

        if (currentRow.length >= MAX_PER_ROW) {
          pushRow();
        }

        i++;
        continue;
      }

      const next = items[i + 1];

      if (next && next.orientation === 'landscape') {

        currentRow.push({
          type: 'landscape-pair',
          items: [item, next]
        });

        if (currentRow.length >= MAX_PER_ROW) {
          pushRow();
        }

        i += 2;
        continue;
      }

      currentRow.push({
        type: 'landscape-single',
        items: [item]
      });

      if (currentRow.length >= MAX_PER_ROW) {
        pushRow();
      }

      i++;
    }
  }

  pushRow();

  return rows;
}

const RectCard = React.memo(function RectCard({ item, onClick, isNew }) {
  return (
    <div
      className={`rect-item ${isNew ? 'is-new' : ''}`}
      onClick={(e) => {
        e.preventDefault();     // 🔥 stop navigation
        e.stopPropagation();    // 🔥 stop bubbling
        onClick(item);          // pass only item
      }}
    >
      <div className="card-frame">
        <img src={item.url} alt="" />
      </div>
    </div>
  );
})




export default function DisplayGallery() {
  const { id } = useParams(); // configId

  const [config, setConfig] = useState(null);
  const [datasetId, setDatasetId] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // const [containerWidth, setContainerWidth] = useState(1200);
  // const containerRef = useRef(null);

  const idsRef = useRef(new Set());
  const esRef = useRef(null);
  const newIdsRef = useRef(new Set());

  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const selectedIdRef = useRef(null);
  selectedIdRef.current = selected?._id;
  const [printCount, setPrintCount] = useState(1);
  // print state
  const [printState, setPrintState] = useState('idle');
  const [uncertainRequest, setUncertainRequest] = useState(null);
  const [accountingRevision, setAccountingRevision] = useState(0);
  const printInFlight = useRef(false);
  const globalPrintLockRef = useRef(0);
  const [, setTick] = useState(0);
  const [, forceRender] = useState(0);
  const forceUpdate = () => setTick(t => t + 1);
  const isLocked = printState === 'sending' || Date.now() < globalPrintLockRef.current;
  const remaining = Math.max(
    0,
    globalPrintLockRef.current - Date.now()
  );


  useEffect(() => {

    document.body.classList.add('gallery-fullscreen');

    document.body.style.background =
      config?.bg || '#0b1220';

    return () => {

      document.body.classList.remove('gallery-fullscreen');

      document.body.style.background = '';

    };

  }, [config?.bg]);

  // useEffect(() => {
  //
  //   const blockMouseButtons = (e) => {
  //
  //     // 1 = middle mouse
  //     // 2 = right mouse
  //
  //     if (e.button === 1 || e.button === 2) {
  //       e.preventDefault();
  //       return false;
  //     }
  //   };
  //
  //   const blockContextMenu = (e) => {
  //     e.preventDefault();
  //     return false;
  //   };
  //
  //   document.addEventListener(
  //     'mousedown',
  //     blockMouseButtons
  //   );
  //
  //   document.addEventListener(
  //     'contextmenu',
  //     blockContextMenu
  //   );
  //
  //   return () => {
  //
  //     document.removeEventListener(
  //       'mousedown',
  //       blockMouseButtons
  //     );
  //
  //     document.removeEventListener(
  //       'contextmenu',
  //       blockContextMenu
  //     );
  //
  //   };
  //
  // }, []);

  function getEffectiveView(items, primaryView) {
    if (primaryView !== 'auto') return primaryView;

    let landscape = 0;
    let portrait = 0;

    for (const item of items) {
      if (item.orientation === 'landscape') landscape++;
      else portrait++;
    }

    // default fallback
    if (landscape === 0 && portrait === 0) return 'landscape';

    return landscape >= portrait ? 'landscape' : 'portrait';
  }


  // ---------------------------
  // Resize tracking
  // ---------------------------
  // useEffect(() => {
  //   const resize = () => {
  //     if (containerRef.current) {
  //       setContainerWidth(containerRef.current.offsetWidth || 1200);
  //     }
  //   };
  //
  //   resize();
  //   window.addEventListener('resize', resize);
  //   return () => window.removeEventListener('resize', resize);
  // }, []);




  useEffect(() => {
    const interval = setInterval(() => {
      const lockUntil = globalPrintLockRef.current || 0;

      if (Date.now() >= lockUntil) return;
      forceUpdate();
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // ---------------------------
  // Fullscreen body class
  // ---------------------------
  useEffect(() => {
    document.body.classList.add('gallery-fullscreen');
    return () => document.body.classList.remove('gallery-fullscreen');
  }, []);

  // ---------------------------
  // Normalize API response
  // ---------------------------
  const normalize = (data) => {
    if (!data) return [];

    const arr = Array.isArray(data)
      ? data
      : Array.isArray(data.items)
        ? data.items
        : [];

    const out = [];
    const seen = new Set();

    for (const r of arr) {
      const itemId = r._id || r.id;
      const url = r.outputUrl || r.url;
      if (!itemId || !url || seen.has(itemId)) continue;

      seen.add(itemId);

      const orientation =
        r.orientation ||
        (r.width && r.height
          ? (r.width >= r.height ? 'landscape' : 'portrait')
          : 'landscape');

      // 🔥 CRITICAL: force portrait pairing
      let aspect =
        orientation === 'portrait'
          ? 0.5
          : (r.width && r.height ? r.width / r.height : 1.59);

        out.push({
          _id: itemId,
          url,
          createdAt: r.createdAt,
          orientation,
          aspect
        });
    }

    return out;
  };

  // ---------------------------
  // Load config first
  // ---------------------------
  useEffect(() => {
    let cancelled = false;

    async function loadConfig() {
      if (!id) return;

      try {
        const cfg = await apiFetch(`/gallery/settings/item/${id}`);
        if (cancelled) return;
        console.log("cfg:", cfg)
        const nextConfig = {
          bg: cfg?.bg || '#0b1220',
          size: cfg?.size || 'medium',
          columns: Number(cfg?.columns || 4),
          primaryView: cfg?.primaryView || 'landscape',
          printDelayMs: Number(cfg?.printDelayMs || 5000),
          maxCopies: Number(cfg?.maxCopies || 3),
          name: cfg?.name || '',
          datasetId: cfg?.datasetId?._id || cfg?.datasetId || '',
          allowDelete: cfg?.allowDelete === true
        };
        console.log("nextConfig:", nextConfig)
        setConfig(nextConfig);
        setDatasetId(prev => {
          if (prev === nextConfig.datasetId) return prev;
          return nextConfig.datasetId;
        });
      } catch (e) {
        console.error('[DisplayGallery] config load failed', e);
        if (!cancelled) {
          setConfig({
            bg: '#0b1220',
            size: 'medium',
            printDelayMs: 5000,
            maxCopies: 3,
            name: '',
            datasetId: ''
          });
        }
      }
    }

    loadConfig();

    return () => { cancelled = true; };
  }, [id]);


  useEffect(() => {
    let cancelled = false;

    async function initialLoad() {
      if (!datasetId) return;

      setLoading(true);

      try {
        let data;

        try {
          data = await apiFetch(`/gallery/photos/list/${datasetId}`);
        } catch {
          data = await apiFetch('/gallery/photos/list', {
            method: 'POST',
            body: { datasetId }
          });
        }

        if (cancelled) return;

        const base = normalize(data);
        idsRef.current = new Set(base.map(b => b._id));
        setItems(base);

      } catch (e) {
        console.error('[DisplayGallery] initial photo load failed', e);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    initialLoad();

    return () => {
      cancelled = true;
    };

  }, [datasetId]);


  useEffect(() => {
  if (!datasetId) return;

  // 🔥 prevent duplicate connections
  if (esRef.current) {
    console.log("⚠ SSE already running — skipping re-init");
    return;
  }

  console.log("🚀 Starting SSE...");

  const es = new EventSource(
    `${SERVER_URL}/gallery/stream?datasetId=${encodeURIComponent(datasetId)}`
  );

  esRef.current = es;

  es.onopen = () => {
    console.log("✅ SSE CONNECTED");
  };

  es.onerror = (e) => {
    console.error("❌ SSE ERROR", e);
  };

  es.onmessage = (e) => {
    console.log("📨 RAW SSE MESSAGE:", e.data);
  };

  es.addEventListener('new-image', (e) => {
    try {
      const payload = JSON.parse(e.data);
      const newId = payload?._id;
      const newUrl = payload?.url;

      if (!newId || !newUrl) return;
      if (idsRef.current.has(newId)) return;

      idsRef.current.add(newId);

      setItems(prev => {
        newIdsRef.current.add(newId);

        forceRender(v => v + 1);

        setTimeout(() => {
          newIdsRef.current.delete(newId);
          forceRender(v => v + 1);
        }, 600);

        const next = [
          {
            _id: newId,
            url: newUrl,
            createdAt: payload.createdAt,
            updatedAt: payload.updatedAt,
            orientation: payload.orientation || 'landscape',
            aspect: payload.orientation === 'portrait'
              ? 0.5
              : 1.59
          },
          ...prev
        ];

        // 🔥 ALWAYS REBUILD ORDER
        next.sort((a, b) => {

          const ad =
            new Date(a.createdAt || 0).getTime();

          const bd =
            new Date(b.createdAt || 0).getTime();

          return bd - ad;
        });

        return next;
      });

    } catch (err) {
      console.error('[DisplayGallery] SSE parse error', err);
    }
  });

  return () => {
    console.log("🛑 Closing SSE");
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
  };

}, [datasetId]);


const effectiveView = useMemo(
  () => getEffectiveView(items, config?.primaryView),
  [items, config?.primaryView]
);

const rows = useMemo(
  () => buildRows(items, effectiveView, config?.columns || 6),
  [items, effectiveView, config?.columns]
);

  // ---------------------------
  // Modal controls
  // ---------------------------
  const openModal = useCallback((it) => {
    setSelected(it);
    setPrintCount(1);
    setPrintState('idle');
    setModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setSelected(null);
    setPrintCount(1);
    setPrintState('idle');
  };

  const inc = () =>
    setPrintCount(c => Math.min(maxCopies, c + 1));

  const dec = () =>
    setPrintCount(c => Math.max(1, c - 1));

  // ---------------------------
  // Print
  // ---------------------------
  async function handlePrint() {
    if (!selected?._id) return;

    const now = Date.now();
    const lockUntil = globalPrintLockRef.current || 0;
    // 🔥 block if still within delay window
    if (now < lockUntil) return;

    if (printInFlight.current) return;
    printInFlight.current = true;
    const dataDocId = selected._id;
    setPrintState('sending');
    const result = await requestPrint('/gallery/photos/print', {
      dataDocId, count: printCount
    });
    if (selectedIdRef.current === dataDocId) {
      setPrintState(admissionMessage(result));
      setUncertainRequest(result?.admissionState === 'uncertain' ? { dataDocId, jobId: result.jobId } : null);
    }
    setAccountingRevision(v => v + 1);
    if (result?.admitted === true) {
      // UX cooldown only; expiry provides no execution evidence.
      globalPrintLockRef.current = Date.now() + (config?.printDelayMs || 5000) + (printCount - 1) * 1000;
    }
    printInFlight.current = false;
  }

  const handleDelete = async () => {
    if (!selected?._id) return;
    try {
      await toast.promise(
        apiFetch('/gallery/photos/delete', { method: 'POST', body: { docId: selected._id } }),
        { pending: 'Deleting…', success: 'Deleted.', error: 'Delete failed.' }
      );
      setItems(prev => prev.filter(x => x._id !== selected._id));
      idsRef.current.delete(selected._id);
      closeModal();
    } catch {}
  };



  function Spinner() {
    return (<>
        <div className="spinner" />
      </>);
  }

  const maxCopies = Math.max(1, Math.min(3, Math.floor(Number(config?.maxCopies) || 3)));
  const requestedCopies = Number(printCount || 1);
  const columns = Number(config?.columns || 12);

  // ---------------------------
  // UI
  // ---------------------------
  if (!id) {
    return <div className="gallery-wrap"><div className="gallery-empty">No gallery configuration selected.</div></div>;
  }

  if (!config) {
    return <div className="gallery-wrap"><div className="gallery-empty">Loading configuration…</div></div>;
  }

  if (!datasetId) {
    return <div className="gallery-wrap"><div className="gallery-empty">This gallery configuration does not have a dataset selected.</div></div>;
  }

  function getRectKey(r) {
    return r.items.map(i => i._id).join('-');
  }

const NEW_COUNT = 4; // animate top 4 items
// console.log(rows.map(r => r.items.map(i => i.orientation)));
// console.log(rows.map(r => r.items.length));
  return (
    <div
      className="gallery-rows gallery-scroll-root"
      style={{
        backgroundColor: config.bg || '#0b1220'
      }}
    >
      <div
        className="gallery-wrap"
        style={{ background: config.bg || '#0b1220' }}
      >
        {loading && items.length === 0 ? (
          <div className="gallery-empty">Loading gallery…</div>
        ) : items.length === 0 ? (
          <div className="gallery-empty">No images available yet.</div>
        ) : (
          <div
            className="gallery-rows"
            key={items.map(i => i._id).join('-')}
          >
          {rows.map((row, rowIndex) => (
            <div
              key={row.map(r => getRectKey(r)).join('|')}
              className="gallery-row"
            >
              {row.map((r) => (
                <div
                  className={`rect ${r.type}`}
                  style={{
                    flex: `1 1 calc(${100 / columns}% - 12px)`,
                    maxWidth: `calc(${100 / columns}% - 12px)`
                  }}
                >

              {r.type === 'landscape' && (
                <RectCard
                  item={r.items[0]}
                  onClick={openModal}
                  isNew={newIdsRef.current.has(r.items[0]._id)}
                />
              )}

              {r.type === 'portrait-pair' && (
                <>
                  <RectCard
                    item={r.items[0]}
                    onClick={openModal}
                    isNew={newIdsRef.current.has(r.items[0]._id)}
                  />
                  <RectCard
                    item={r.items[1]}
                    onClick={openModal}
                    isNew={newIdsRef.current.has(r.items[1]._id)}
                  />
                </>
              )}

              {r.type === 'portrait-single' && (
                <>
                  <div className="rect-item empty" />
                  <RectCard
                    item={r.items[0]}
                    onClick={openModal}
                    isNew={newIdsRef.current.has(r.items[0]._id)}
                  />
                </>
              )}

              {r.type === 'landscape-pair' && (
                <>
                  <RectCard
                    item={r.items[0]}
                    onClick={openModal}
                    isNew={newIdsRef.current.has(r.items[0]._id)}
                  />
                  <RectCard
                    item={r.items[1]}
                    onClick={openModal}
                    isNew={newIdsRef.current.has(r.items[1]._id)}
                  />
                </>
              )}

              {r.type === 'landscape-single' && (
                <>
                  <div className="rect-item empty" />
                  <RectCard
                    item={r.items[0]}
                    onClick={openModal}
                    isNew={newIdsRef.current.has(r.items[0]._id)}
                  />
                </>
              )}

              {r.type === 'portrait' && (
                <RectCard
                  item={r.items[0]}
                  onClick={openModal}
                  isNew={newIdsRef.current.has(r.items[0]._id)}
                />
              )}
              </div>
            ))}
          </div>
          ))}
          </div>
        )}
      </div>
      {modalOpen && selected && (
        <div className="gallery-modal-backdrop" onClick={closeModal}>
          <div className="gallery-modal" onClick={(e) => e.stopPropagation()}>

            <div className="gallery-modal-body">
              {printState !== 'idle' && <p role="status">{printState === 'sending' ? 'Sending request...' : printState}</p>}
              <PrintAccounting key={selected._id} dataDocId={selected._id} revision={accountingRevision}
                uncertainJobId={uncertainRequest?.dataDocId === selected._id ? uncertainRequest.jobId : undefined} />

              <div className="image-square" style={{ position: 'relative' }}>
                <img src={selected.url} alt="" />

                {isLocked && (
                  <div
                    className="gallery-print-overlay"
                  >
                    <div className="gallery-print-loader" />
                    <div className="gallery-print-text">
                      Please wait before another request...
                    </div>
                  </div>
                )}
              </div>

              <div
                className="print-row"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 20,
                  flexWrap: 'wrap',
                  marginTop: 15,
                  backgroundColor: '#f1f5f9'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {(config?.maxCopies || 1) > 1 && (
                    <div className="print-controls-group">
                      <span className="print-label">Copies per request</span>

                      <button
                        type="button"
                        className="btn print-controls"
                        onClick={dec}
                        disabled={isLocked || printCount <= 1}
                      >
                        -
                      </button>

                      <span className="print-label">{printCount}</span>

                      <button
                        type="button"
                        className="btn print-controls"
                        onClick={inc}
                        disabled={isLocked || printCount >= maxCopies}
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {isLocked ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#666' }}>
                      <Spinner />
                      <span>Please wait before another request...</span>
                    </div>
                  ) : (
                    <button type="button" className="btn-green" onClick={handlePrint}>
                      Print
                    </button>
                  )}

                  <div
                    className="print-counter"
                  >
                  {Math.min(requestedCopies, maxCopies)}
                  <span>/</span>
                  {maxCopies}
                  </div>

                  {config?.allowDelete !== false && (
                    <button type="button" className="btn-red" onClick={handleDelete}>
                      Delete
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
